/**
 * mejoin (me-11..me-14): Karr Me's sign-up both ways and the family, as pure rules.
 *
 * A person signs up for their own year at the door ("Just me"), shares it with
 * family (Can see, Can prepare), or a family head makes a year for a son or a
 * daughter, who lands straight in it on signing up with that email. The server
 * decides every one of these (`me_family_*`); this file holds the words and the
 * small readings the screens make before they ask: is this a person's PAN, who
 * is this year to me, how old is the child.
 *
 * PURE, so `join.test.ts` pins every branch.
 */

/** The org type of a person's own year (mejoin). Never a business: My businesses skips it. */
export const INDIVIDUAL_ORG_TYPE = 'in_individual';

export function isIndividualOrg(orgType: string | null | undefined): boolean {
    return orgType === INDIVIDUAL_ORG_TYPE;
}

/** What a PAN's fourth letter says it is. */
export type PanKind = 'person' | 'huf' | 'business' | 'incomplete';

export function cleanPan(raw: string): string {
    return (raw || '').replace(/\s/g, '').toUpperCase();
}

export function panKind(raw: string): PanKind {
    const pan = cleanPan(raw);
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)) return 'incomplete';
    const fourth = pan[3];
    if (fourth === 'P') return 'person';
    if (fourth === 'H') return 'huf';
    return 'business';
}

/** The relation as typed: who the family member is, in the words of whoever typed it. */
export type Relation = 'spouse' | 'son' | 'daughter' | 'father' | 'mother' | 'huf' | 'other';

export const RELATIONS: readonly { value: Relation; label: string }[] = [
    { value: 'spouse', label: 'Spouse' },
    { value: 'son', label: 'Son' },
    { value: 'daughter', label: 'Daughter' },
    { value: 'father', label: 'Father' },
    { value: 'mother', label: 'Mother' },
    { value: 'huf', label: 'HUF' },
    { value: 'other', label: 'Other' },
];

/** Who a year's person is to the reader, as `me_family_mine` turns it round. */
export type RelationToMe = Relation | 'parent' | 'child';

const TO_ME_LABEL: Record<RelationToMe, string> = {
    spouse: 'Spouse', son: 'Son', daughter: 'Daughter', father: 'Father', mother: 'Mother',
    parent: 'Parent', child: 'Child', huf: 'HUF', other: 'Family',
};

export function relationLabel(r: RelationToMe | null | undefined): string {
    return r ? TO_ME_LABEL[r] ?? 'Family' : 'Family';
}

/**
 * The door's "your father set it up": `relation` on a made year is what the
 * person is to the head, so the head is the other way round.
 */
export function headWords(personToHead: Relation | null | undefined): string {
    switch (personToHead) {
        case 'son': case 'daughter': return 'your parent';
        case 'father': case 'mother': return 'your son or daughter';
        case 'spouse': return 'your spouse';
        default: return 'a family member';
    }
}

export type Access = 'see' | 'prepare';

export const ACCESS_CHOICES: readonly { value: Access; label: string; line: string }[] = [
    { value: 'see', label: 'Can see', line: 'Sees the year, the computation and the return. Changes nothing.' },
    { value: 'prepare', label: 'Can prepare', line: 'Adds income, proofs and claims, and prepares the return. Only the person signs it.' },
];

export function accessLabel(a: Access | 'owner' | null | undefined): string {
    if (a === 'owner') return 'Keeps it';
    return ACCESS_CHOICES.find((c) => c.value === a)?.label ?? 'Can see';
}

/** Under 18 on `today` (YYYY-MM-DD), from a date of birth: the head stays owner until the birthday. */
export function isUnder18(dob: string, today: string): boolean {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) return false;
    const [y, m, d] = dob.split('-').map(Number);
    const eighteen = `${String(y + 18).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    return eighteen > today;
}

/** "Nia Rather" → "Nia Rather's year": the header of a person's year. */
export function yearTitle(name: string | null | undefined): string {
    const n = (name ?? '').trim();
    if (!n) return 'Karr Me';
    return n.endsWith('s') ? `${n}' year` : `${n}'s year`;
}

/** The door's answer when a PAN already has a year, in words. */
export function existingYearWords(r: { status: string; keeper?: string; relation?: Relation | null }): string {
    if (r.status === 'made_by_family') {
        return `Your Karr Me is already set up: ${headWords(r.relation)} (${r.keeper ?? 'someone'}) made it for you on another email address. Ask to join it, and it becomes yours.`;
    }
    return `A Karr Me for this PAN is kept by ${r.keeper ?? 'someone'}. Karr shows you nothing of it. Ask to join, and they decide.`;
}
